import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { BRAND_LOGO_PUBLIC_PATH } from '../config/branding';

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  showBackButton?: boolean;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ 
  children, 
  title, 
  subtitle, 
  showBackButton = true 
}) => {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-mesh-gradient flex items-center justify-center p-4 sm:p-6 lg:p-8 animate-fadeIn">
      {/* Container Card */}
      <div className="w-full max-w-5xl bg-white dark:bg-slate-800 rounded-3xl overflow-hidden auth-card-shadow flex flex-col lg:flex-row min-h-[600px] border border-white/10">
        
        {/* Left Side: Visual Content (Desktop/Tablet) */}
        <div className="hidden lg:flex lg:w-5/12 bg-[#0ea5e9] relative overflow-hidden flex-col justify-between p-10 text-white">
          {/* Abstract background shapes */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-500/20 rounded-full -ml-40 -mb-40 blur-3xl"></div>
          
          <div className="relative z-10">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="bg-white/20 p-2 rounded-full backdrop-blur-sm group-hover:bg-white/30 transition-all">
                <img src={BRAND_LOGO_PUBLIC_PATH} alt="Logo" className="w-8 h-8 object-contain rounded-full" />
              </div>
              <span className="text-xl font-bold tracking-tight">{t('brand')}</span>
            </Link>
            
            <div className="mt-20">
              <h1 className="text-4xl font-extrabold leading-tight mb-4">
                {title}
              </h1>
              {subtitle && (
                <p className="text-blue-100 text-lg leading-relaxed max-w-xs">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          
          <div className="relative z-10 text-sm text-blue-100/60 font-medium">
            © {new Date().getFullYear()} {t('brand')}. All rights reserved.
          </div>
        </div>

        {/* Right Side: Form Content */}
        <div className="flex-1 flex flex-col p-6 sm:p-10 lg:p-12 relative">
          {/* Brand Logo for Mobile */}
          <div className="lg:hidden flex items-center justify-between mb-8">
            <Link to="/" className="flex items-center gap-2">
              <img src={BRAND_LOGO_PUBLIC_PATH} alt="Logo" className="w-10 h-10 object-contain rounded-full" />
              <span className="font-bold text-slate-900 dark:text-white">{t('brand')}</span>
            </Link>
          </div>

          {showBackButton && (
            <Link 
              to="/" 
              className="absolute top-6 left-6 lg:top-10 lg:left-10 text-slate-400 hover:text-primary-500 transition-colors flex items-center gap-1.5 text-sm font-medium group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              {t('nav.home')}
            </Link>
          )}

          <div className="max-w-md w-full mx-auto my-auto py-8">
            <div className="lg:hidden mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{title}</h2>
              {subtitle && <p className="text-slate-500 dark:text-slate-400 text-sm">{subtitle}</p>}
            </div>

            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
